import axios from "axios";

export const RPPG_CHUNK_DURATION_MS = 2000;
export const RPPG_QUICK_VITALS_START_CHUNK = 5;
export const RPPG_QUICK_VITALS_INTERVAL_MS = 5000;
export const RPPG_VITAL_PREDICT_CHUNK = 25;
export const RPPG_MAX_CHUNKS = 35;
export const RPPG_SCAN_DURATION_MS = RPPG_MAX_CHUNKS * RPPG_CHUNK_DURATION_MS;
export const RPPG_RESULT_POLL_INTERVAL_MS = 2500;
export const RPPG_RESULT_POLL_MAX_MS = 90_000;
export const RPPG_CHUNK_UPLOAD_MAX_RETRIES = 3;
/** Consecutive abort_recommended polls before offering continue-or-abort. */
export const RPPG_ABORT_RECOMMENDED_STREAK = 3;

export type RppgQualityReasonCode =
  | 'POOR_LIGHTING'
  | 'HOLD_STILL'
  | 'WEAK_SIGNAL'
  | 'FACE_NOT_VISIBLE'
  | 'LOW_FRAME_RATE';

export type RppgConfidenceLevel = 'low' | 'medium' | 'high';

export interface RppgCaptureQuality {
  score: number;
  stars: number;
  reasons: RppgQualityReasonCode[];
  abort_recommended: boolean;
}

export interface RppgQuickQuality {
  n_chunks_used: number;
  n_chunks_available: number;
  snr_db: number;
  snr_category: RppgConfidenceLevel;
  capture_quality?: RppgCaptureQuality;
}

export interface RppgRescanQuality {
  recommended: boolean;
  reasons: RppgQualityReasonCode[];
  n_signals?: number;
}

export interface RppgHeartRate {
  bpm: number;
  confidence: RppgConfidenceLevel;
}

export interface RppgRespiratoryRate {
  rpm: number;
  confidence: RppgConfidenceLevel;
}

export interface RppgSpo2 {
  percent: number;
  method?: string;
}

export interface RppgHrv {
  rmssd_ms: number;
  sdnn_ms: number;
  pnn50_pct?: number;
  mean_rri_ms: number;
  sd1_ms: number;
  sd2_ms: number;
  lf_hf_ratio: number;
  n_beats?: number;
  confidence?: {
    rmssd_ms?: RppgConfidenceLevel;
    sdnn_ms?: RppgConfidenceLevel;
    sd1_ms?: RppgConfidenceLevel;
    sd2_ms?: RppgConfidenceLevel;
    mean_rri_ms?: RppgConfidenceLevel;
    pnn50_pct?: RppgConfidenceLevel;
  };
}

export interface RppgStressIndices {
  prq: number;
  sns_index: number;
  pns_index: number;
  stress_index: number | null;
  stress_confidence?: RppgConfidenceLevel;
  prq_confidence?: RppgConfidenceLevel;
}

export interface RppgWellness {
  wellness_index: number;
  wellness_level: number;
  wellness_label?: string;
  components?: Record<string, number>;
  n_parameters?: number;
}

export interface RppgUploadInitResponse {
  reading_id: string;
  bucket: string;
  key_prefix: string;
  key_template: string;
  expires_in: number;
  max_chunk_bytes: number;
  upload: {
    url: string;
    fields: Record<string, string>;
  };
}

export interface RppgDemographics {
  age?: number;
  gender?: string;
  height_cm?: number;
  weight_kg?: number;
  diabetes?: string;
}

export interface RppgUploadMetadata {
  schema_version: 1;
  client_name: string;
  device: {
    platform: "web";
    os: string;
    os_version: string;
    app_version?: string;
    browser?: string;
    browser_version?: string;
    model?: string;
    camera?: {
      facing: "front";
      requested_fps: number;
    };
  };
  location?: {
    granularity: "precise" | "coarse";
    source: "gps" | "ip";
    lat?: number | null;
    lng?: number | null;
    alt?: number | null;
    accuracy_m?: number | null;
  };
}

/** Matches MedsiCheckV2 quick-vitals predictions shape. */
export interface RppgQuickVitals {
  heart_rate: RppgHeartRate | null;
  hrv: RppgHrv | null;
  respiratory_rate: RppgRespiratoryRate | null;
  spo2: RppgSpo2 | null;
  stress_indices: RppgStressIndices | null;
  wellness?: RppgWellness | null;
}

export interface RppgQuickVitalsResponse {
  reading_id: string;
  variant: string;
  predictions: RppgQuickVitals;
  quality: RppgQuickQuality | null;
  processing_time_seconds: number;
  status?: string;
}

export interface RppgCaptureStats {
  fps: number;
  drop_pct: number;
  jitter_ratio: number;
  max_gap_ratio: number;
  unstable: boolean;
}

export interface RppgQuality {
  face_detection_rate?: number;
  fused_sqi?: number;
  n_chunks_used?: number;
  n_chunks_available?: number;
  snr_db?: number;
  snr_category?: RppgConfidenceLevel;
  signal_snr_db?: number;
  signal_snr_category?: string;
  exposure_wobble?: number;
  capture?: RppgCaptureStats;
  rescan?: RppgRescanQuality;
  [key: string]: unknown;
}

export interface RppgFinalResult {
  reading_id?: string;
  model_version?: string;
  variant?: string;
  predictions: Record<string, unknown>;
  quality?: RppgQuality | null;
  demographics?: Record<string, unknown> | null;
  predicted_at?: string;
  processing_time_seconds?: number;
  result_s3_uri?: string;
  [key: string]: unknown;
}

const OVERRIDDEN_PRESIGNED_FIELDS = new Set(["key", "Content-Type"]);

export class RppgGatewayTimeoutError extends Error {
  constructor() {
    super("vital-predict gateway timeout — fall back to polling");
    this.name = "RppgGatewayTimeoutError";
  }
}

function buildRppgClient(apiKey: string, baseUrl: string) {
  return axios.create({
    baseURL: baseUrl.replace(/\/$/, ""),
    headers: {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    },
    timeout: 35_000,
  });
}

function isRppgProcessingResponse(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { status?: string }).status === "processing"
  );
}

export function isRppgResultReady(data: unknown): data is RppgFinalResult {
  if (!data || typeof data !== "object" || isRppgProcessingResponse(data)) {
    return false;
  }
  return Boolean((data as RppgFinalResult).predictions);
}

function shouldPollAfterVitalPredictError(status: number | undefined): boolean {
  return (
    status === 502 ||
    status === 504 ||
    (typeof status === "number" && status >= 500)
  );
}

export async function rppgUploadInit(
  apiKey: string,
  baseUrl: string,
  readingId?: string,
  metadata?: RppgUploadMetadata,
): Promise<RppgUploadInitResponse> {
  const client = buildRppgClient(apiKey, baseUrl);
  const body: Record<string, unknown> = {};
  if (readingId) {
    body.reading_id = readingId;
  }
  if (metadata) {
    body.metadata = metadata;
  }
  const response = await client.post<RppgUploadInitResponse>(
    "/v1/upload-init",
    body,
  );
  return response.data;
}

export async function rppgUploadChunk(
  uploadUrl: string,
  fields: Record<string, string>,
  keyPrefix: string,
  chunkIndex: number,
  chunkBlob: Blob,
): Promise<number> {
  const form = new FormData();

  for (const [k, v] of Object.entries(fields)) {
    if (OVERRIDDEN_PRESIGNED_FIELDS.has(k)) {
      continue;
    }
    form.append(k, v);
  }

  const chunkKey = `${keyPrefix}chunk_${chunkIndex}/video.mp4`;
  form.append("key", chunkKey);
  form.append("Content-Type", "video/mp4");

  const uploadBlob =
    chunkBlob.type === "video/mp4"
      ? chunkBlob
      : new Blob([chunkBlob], { type: "video/mp4" });
  form.append("file", uploadBlob, `chunk_${chunkIndex}.mp4`);

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: form,
  });

  if (response.status !== 204 && response.status !== 201) {
    throw new Error(
      `Chunk ${chunkIndex} upload failed with status ${response.status}`,
    );
  }

  return response.status;
}

export async function rppgQuickVitals(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  nChunks: number,
): Promise<RppgQuickVitalsResponse | null> {
  const client = buildRppgClient(apiKey, baseUrl);
  try {
    const response = await client.post<RppgQuickVitalsResponse>(
      "/v1/quick-vitals",
      { reading_id: readingId, n_chunks: nChunks },
      { validateStatus: (status) => status === 200 || status === 202 },
    );
    if (response.status === 202 || isRppgProcessingResponse(response.data)) {
      return null;
    }
    if (!response.data?.predictions) {
      return null;
    }
    return {
      reading_id: response.data.reading_id || readingId,
      variant: response.data.variant || 'fast',
      predictions: response.data.predictions,
      quality: response.data.quality ?? null,
      processing_time_seconds: response.data.processing_time_seconds ?? 0,
      status: response.data.status,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn("[rPPG] quick-vitals error:", message, {
      reading_id: readingId,
      n_chunks: nChunks,
    });
    return null;
  }
}

export async function rppgVitalPredict(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  demographics: RppgDemographics,
  nChunks: number = RPPG_VITAL_PREDICT_CHUNK,
): Promise<RppgFinalResult> {
  const client = buildRppgClient(apiKey, baseUrl);
  const requestBody = {
    reading_id: readingId,
    demographics,
    n_chunks: nChunks,
  };

  const postPredict = () =>
    client.post<RppgFinalResult>("/v1/vital-predict", requestBody);

  try {
    const response = await postPredict();
    return response.data;
  } catch (err: unknown) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (shouldPollAfterVitalPredictError(status)) {
      try {
        const retryResponse = await postPredict();
        return retryResponse.data;
      } catch (retryErr: unknown) {
        const retryStatus = axios.isAxiosError(retryErr)
          ? retryErr.response?.status
          : undefined;
        if (shouldPollAfterVitalPredictError(retryStatus)) {
          throw new RppgGatewayTimeoutError();
        }
        throw retryErr;
      }
    }
    throw err;
  }
}

export async function rppgGetResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
): Promise<RppgFinalResult | null> {
  const client = buildRppgClient(apiKey, baseUrl);
  const response = await client.get<RppgFinalResult & { status?: string }>(
    "/v1/result",
    {
      params: { rid: readingId, variant: "fast" },
      validateStatus: (status) => status === 200 || status === 202,
    },
  );

  if (response.status === 202 || !isRppgResultReady(response.data)) {
    return null;
  }

  return response.data;
}

export async function rppgPollFastResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
): Promise<RppgFinalResult | null> {
  const deadline = Date.now() + RPPG_RESULT_POLL_MAX_MS;

  while (Date.now() < deadline) {
    const result = await rppgGetResult(apiKey, baseUrl, readingId);
    if (result !== null) {
      return result;
    }
    await new Promise<void>((resolve) =>
      setTimeout(resolve, RPPG_RESULT_POLL_INTERVAL_MS),
    );
  }

  return null;
}

export async function rppgResolveFinalResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  demographics: RppgDemographics,
  nChunks: number = RPPG_VITAL_PREDICT_CHUNK,
): Promise<RppgFinalResult | null> {
  try {
    const result = await rppgVitalPredict(
      apiKey,
      baseUrl,
      readingId,
      demographics,
      nChunks,
    );
    if (isRppgResultReady(result)) {
      return result;
    }
  } catch (err) {
    if (!(err instanceof RppgGatewayTimeoutError)) {
      console.warn("[rPPG] vital-predict error, falling back to poll:", err);
    }
  }

  return rppgPollFastResult(apiKey, baseUrl, readingId);
}

export async function uploadChunkWithRetry(
  uploadUrl: string,
  fields: Record<string, string>,
  keyPrefix: string,
  chunkIndex: number,
  chunkBlob: Blob,
  maxRetries = RPPG_CHUNK_UPLOAD_MAX_RETRIES,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await rppgUploadChunk(
        uploadUrl,
        fields,
        keyPrefix,
        chunkIndex,
        chunkBlob,
      );
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(
        `[rPPG] chunk ${chunkIndex} upload attempt ${attempt + 1}/${maxRetries} failed:`,
        message,
      );
    }
  }
  return false;
}
